import {
  Box,
  Button,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import type { ChangeEvent } from "react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useBreadcrumbs } from "../../contexts/BreadcrumbContext";
import { useSnackbar } from "../../contexts/SnackbarContext";
import { createAdminProduct } from "../../services/order.service";
import { ProductCreateRequest, ProductType } from "../../types/order";

type FormState = {
  code: string;
  description: string;
  custom_description: string;
  search_string: string;
  fclo: string;
  design_code: string;
  chromatic_code: string;
  material_code: string;
  index_code: string;
  cylinder_min: string;
  cylinder_max: string;
  sphere_min: string;
  sphere_max: string;
  additional_min: string;
  additional_max: string;
  comm_diameter: string;
  physical_diameter: string;
  product_type: ProductType;
};

const initialForm: FormState = {
  code: "",
  description: "",
  custom_description: "",
  search_string: "",
  fclo: "",
  design_code: "",
  chromatic_code: "",
  material_code: "",
  index_code: "",
  cylinder_min: "",
  cylinder_max: "",
  sphere_min: "",
  sphere_max: "",
  additional_min: "",
  additional_max: "",
  comm_diameter: "",
  physical_diameter: "",
  product_type: "RX",
};

type FieldDef = {
  key: keyof FormState;
  label: string;
  placeholder: string;
  required?: boolean;
};

const rows: Array<[FieldDef, FieldDef?]> = [
  [
    { key: "code", label: "Code", placeholder: "Enter Code", required: true },
    {
      key: "description",
      label: "Description",
      placeholder: "Enter Description",
      required: true,
    },
  ],
  [
    {
      key: "custom_description",
      label: "Custom Description",
      placeholder: "Enter Custom Description",
      required: true,
    },
    {
      key: "search_string",
      label: "Searchstring",
      placeholder: "Enter Searchstring",
      required: true,
    },
  ],
  [
    { key: "fclo", label: "Fclo", placeholder: "Enter Fclo", required: true },
    {
      key: "design_code",
      label: "Design Code",
      placeholder: "Enter Design Code",
      required: true,
    },
  ],
  [
    {
      key: "chromatic_code",
      label: "Chromatic Code",
      placeholder: "Enter Chromatic Code",
      required: true,
    },
    {
      key: "material_code",
      label: "Material Code",
      placeholder: "Enter Material Code",
      required: true,
    },
  ],
  [
    {
      key: "index_code",
      label: "Index Code",
      placeholder: "Enter Index Code",
      required: true,
    },
    {
      key: "cylinder_min",
      label: "Cylinder Min",
      placeholder: "Enter Cylinder Min",
      required: true,
    },
  ],
  [
    {
      key: "cylinder_max",
      label: "Cylinder Max",
      placeholder: "Enter Cylinder Max",
      required: true,
    },
    {
      key: "sphere_min",
      label: "Sphere Min",
      placeholder: "Enter Sphere Min",
      required: true,
    },
  ],
  [
    {
      key: "sphere_max",
      label: "Sphere Max",
      placeholder: "Enter Sphere Max",
      required: true,
    },
    {
      key: "additional_min",
      label: "Addition Min",
      placeholder: "Enter Addition Min",
      required: true,
    },
  ],
  [
    {
      key: "additional_max",
      label: "Addition Max",
      placeholder: "Enter Addition Max",
      required: true,
    },
    {
      key: "comm_diameter",
      label: "Comm Diameter",
      placeholder: "Enter Comm Diameter",
      required: true,
    },
  ],
  [
    {
      key: "physical_diameter",
      label: "Physical Diameter",
      placeholder: "Enter Physical Diameter",
      required: true,
    },
  ],
];

const requiredFields: Array<keyof FormState> = [
  "code",
  "description",
  "custom_description",
  "search_string",
  "fclo",
  "design_code",
  "chromatic_code",
  "material_code",
  "index_code",
  "cylinder_min",
  "cylinder_max",
  "sphere_min",
  "sphere_max",
  "additional_min",
  "additional_max",
  "comm_diameter",
  "physical_diameter",
];

export default function CrmProductMasterCreate() {
  const navigate = useNavigate();
  const { setBreadcrumbs } = useBreadcrumbs();
  const { showSnackbar } = useSnackbar();
  const [form, setForm] = useState<FormState>(initialForm);
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setBreadcrumbs([
      { label: "Product Master", to: "/crm/product-master" },
      { label: "Create Product Master", to: "/crm/product-master/create" },
    ]);
  }, [setBreadcrumbs]);

  const errors = useMemo(() => {
    if (!submitted) return new Set<string>();

    return new Set(
      requiredFields
        .filter((key) => !String(form[key] || "").trim())
        .map(String),
    );
  }, [form, submitted]);

  const onFieldChange =
    (key: keyof FormState) => (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setForm((prev) => ({ ...prev, [key]: value }));
    };

  const handleSubmit = async () => {
    setSubmitted(true);
    const hasMissingRequired = requiredFields.some(
      (key) => !String(form[key] || "").trim(),
    );
    if (hasMissingRequired) return;

    setIsSubmitting(true);
    try {
      const payload: ProductCreateRequest = {
        code: form.code,
        product_type: form.product_type,
        description: form.description,
        custom_description: form.custom_description,
        search_string: form.search_string,
        fclo: form.fclo,
        design_code: form.design_code,
        chromatic_code: form.chromatic_code,
        material_code: form.material_code,
        index_code: form.index_code,
        cylinder_min: form.cylinder_min,
        cylinder_max: form.cylinder_max,
        sphere_min: form.sphere_min,
        sphere_max: form.sphere_max,
        additional_min: form.additional_min,
        additional_max: form.additional_max,
        comm_diameter: form.comm_diameter,
        physical_diameter: form.physical_diameter,
      };

      await createAdminProduct(payload);
      showSnackbar("Product created successfully", "success");
      navigate("/crm/product-master");
    } catch (error) {
      console.error("Error creating product:", error);
      showSnackbar("Failed to create product", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Stack spacing={3}>
      <Box>
      

        <Paper sx={{ borderRadius: 1, overflow: "hidden" }}>
          <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid #D8DBDF" }}>
            <Typography sx={{ fontSize: 20, fontWeight: 600, lineHeight: 1.4 }}>
              Product Master Info
            </Typography>
          </Box>

          <Stack spacing={2} sx={{ px: 2, pt: 2 }}>
            {rows.map(([left, right]) => (
              <Stack
                key={left.key}
                direction={{ xs: "column", md: "row" }}
                spacing={2}
                alignItems="flex-start"
              >
                <Box sx={{ flex: 1, width: "100%" }}>
                  <Typography sx={{ fontSize: 16, color: "#111", mb: 0.75 }}>
                    {left.label}
                    {left.required && (
                      <Box component="span" sx={{ color: "#BE2217" }}>
                        {" "}
                        *
                      </Box>
                    )}
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder={left.placeholder}
                    value={String(form[left.key] || "")}
                    onChange={onFieldChange(left.key)}
                    error={errors.has(String(left.key))}
                    helperText={
                      errors.has(String(left.key))
                        ? `${left.label} is required`
                        : " "
                    }
                  />
                </Box>

                {right ? (
                  <Box sx={{ flex: 1, width: "100%" }}>
                    <Typography sx={{ fontSize: 16, color: "#111", mb: 0.75 }}>
                      {right.label}
                      {right.required && (
                        <Box component="span" sx={{ color: "#BE2217" }}>
                          {" "}
                          *
                        </Box>
                      )}
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder={right.placeholder}
                      value={String(form[right.key] || "")}
                      onChange={onFieldChange(right.key)}
                      error={errors.has(String(right.key))}
                      helperText={
                        errors.has(String(right.key))
                          ? `${right.label} is required`
                          : " "
                      }
                    />
                  </Box>
                ) : (
                  <Box sx={{ flex: 1, width: "100%" }} />
                )}
              </Stack>
            ))}
          </Stack>

          <Stack
            direction="row"
            justifyContent="flex-end"
            spacing={2}
            sx={{ px: 2, py: 2, mt: 2 }}
          >
            <Button
              variant="outlined"
              onClick={() => navigate("/crm/product-master")}
              sx={{
                borderRadius: "96px",
                borderColor: "#111",
                color: "#111",
                px: 3,
                textTransform: "none",
              }}
              disabled={isSubmitting}
            >
              Discard
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              sx={{
                borderRadius: "96px",
                bgcolor: "#111",
                px: 3,
                textTransform: "none",
                boxShadow: "none",
                "&:hover": { bgcolor: "#000", boxShadow: "none" },
              }}
              disabled={isSubmitting}
            >
              Create
            </Button>
          </Stack>
        </Paper>
      </Box>
    </Stack>
  );
}
